import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  List,
  ListItem,
  CircularProgress,
} from '@mui/material'
import { Close as CloseIcon, Comment as CommentIcon } from '@mui/icons-material'
import { getComentarios, crearComentario } from 'src/services/motosSnopServices'

const ComentariosModal = ({
  open,
  onClose,
  modeloSeleccionado,
  escenario = 'inicial',
  readOnly = false
}) => {
  const [comentarios, setComentarios] = useState([])
  const [nuevoComentario, setNuevoComentario] = useState('')
  const [loading, setLoading] = useState(false)
  const [enviando, setEnviando] = useState(false)

  const fetchComentarios = async () => {
    setLoading(true)
    try {
      const response = await getComentarios(modeloSeleccionado, escenario)
      if (response && response.comentarios) {
        setComentarios(response.comentarios)
      } else {
        setComentarios([])
      }
    } catch (error) {
      console.error('Error al cargar comentarios:', error)
      setComentarios([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && modeloSeleccionado) {
      fetchComentarios()
    }
  }, [open, modeloSeleccionado, escenario])

  const handleCrearComentario = async () => {
    if (!nuevoComentario.trim()) return

    setEnviando(true)
    try {
      const comentarioData = {
        modelo_planeacion: modeloSeleccionado,
        comentario: nuevoComentario.trim(),
        escenario
      }

      const response = await crearComentario(comentarioData)
      if (response) {
        setNuevoComentario('')
        await fetchComentarios() // Recargar comentarios
      }
    } catch (error) {
      console.error('Error al crear comentario:', error)
    } finally {
      setEnviando(false)
    }
  }

  const formatearFecha = (fechaString) => {
    const fecha = new Date(fechaString)
    return fecha.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleClose = () => {
    setNuevoComentario('')
    setComentarios([])
    onClose()
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
          maxHeight: '80vh',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CommentIcon sx={{ color: '#2196f3' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
            Comentarios - {modeloSeleccionado}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '500px' }}>
        {/* Lista de comentarios */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : comentarios.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '200px',
              color: '#666'
            }}>
              <CommentIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="body1">
                No hay comentarios aún
              </Typography>
              <Typography variant="body2" sx={{ color: '#999' }}>
                ¡Sé el primero en agregar un comentario!
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {comentarios.map((comentario, index) => (
                <ListItem key={comentario.id || index} sx={{ p: 0, mb: 2 }}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      width: '100%', 
                      p: 2,
                      backgroundColor: '#fafafa',
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        backgroundColor: '#f0f0f0'
                      }
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mb: 1, 
                        color: '#333',
                        lineHeight: 1.5
                      }}
                    >
                      {comentario.comentario}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#666',
                        fontSize: '0.75rem'
                      }}
                    >
                      {formatearFecha(comentario.fecha)}
                    </Typography>
                  </Paper>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {!readOnly && (
          <>
            <Divider />

            {/* Área para crear nuevo comentario */}
            <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>
                Agregar nuevo comentario
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder="Escribe tu comentario aquí..."
                variant="outlined"
                disabled={enviando}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '&:hover fieldset': {
                      borderColor: '#2196f3',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2196f3',
                    },
                  },
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: 2,
        backgroundColor: '#fafafa',
        borderTop: '1px solid #e0e0e0',
        justifyContent: 'space-between'
      }}>
        <Button
          onClick={handleClose}
          sx={{ color: '#666' }}
        >
          Cerrar
        </Button>
        {!readOnly && (
          <Button
            onClick={handleCrearComentario}
            variant="contained"
            disabled={!nuevoComentario.trim() || enviando}
            sx={{
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
              },
              minWidth: '120px'
            }}
          >
            {enviando ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              'Agregar'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ComentariosModal 